import { AppContext } from "App";

import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { ArrayUtils } from "Global/Base/Utils/ArrayUtils";
import { InvalidOperationException } from "../../Global/Base/Exceptions";
import { ContextType, GlobalContext } from "../../Global/Base/GlobalContext";
import { CancellationToken } from "../../Global/Base/Promises/CancellationTokenSource";
import { IOWObject } from "./DataTypes/IOWObject";
import { OWObject } from "./DataTypes/OWObject";
import { OWManager } from "./OWManager";
import { RequestContextWithoutTransactionContextId } from "./RequestContextWithoutTransactionContextId";

const _referencesSymbol = Symbol.for("__GC_REFERENCE_COUNT__");

// internal veci pro InstanceManager
class createdInstances {
    private static _createdInstances: Map<string, OWObject | OWObject[]>;

    public static get instances(): Map<string, OWObject | OWObject[]> {
        if (!createdInstances._createdInstances) {
            const istMgr = _getMasterManager();
            if (istMgr && (istMgr as any)._instances)
                createdInstances._createdInstances = (istMgr as any)._instances;
            else
                createdInstances._createdInstances = new Map();
        }
        return createdInstances._createdInstances;
    }

    public static getIterator(): Iterable<OWObject | OWObject[]> {
        return createdInstances._getIterator();
    }

    private static *_getIterator(): IterableIterator<OWObject | OWObject[]> {
        for (const val of createdInstances.instances.values())
            yield val;
    }
}

function _getMasterManager(): typeof InstanceManager {
    return self !== top ? GlobalContext.getType<typeof InstanceManager>(InstanceManager, ContextType.top) : null;
}

type LoggableInstance = {
    instanceID: string;
    id: any;
    isNew: boolean;
    objectName: string;
    attributeOnParentObject?: string | null;
};
type TimedInstance = LoggableInstance & { time: number };

const logger = LazyLoadingSectionLogger.create(LoggingSection.objectWrapper);

export class InstanceManager {
    private static __instancesToDelete: TimedInstance[] = [];
    /**
     * Čas po kterém se periodicky posílá uvolnění instancí na službu (ms)
     */
    private static _cleanupInterval: number = 60000;
    /**
     * Handle časovače vyvolávajícího uvolnění instancí
     */
    private static _cleanupHandle: number = 0;
    /**
     * Interval o který se odloží každé volání uvolnění na službu (ms)
     */
    private static readonly _cleanupDelay = 30000;

    private static set _instancesToDelete(instances: TimedInstance[]) {
        const mgr = _getMasterManager();
        mgr ? mgr.__instancesToDelete = instances : InstanceManager.__instancesToDelete = instances;
    }

    private static get _instancesToDelete(): TimedInstance[] {
        const mgr = _getMasterManager();
        return mgr ? mgr.__instancesToDelete : InstanceManager.__instancesToDelete;
    }

    // @ts-ignore: Declared but it's value is never read
    private static get _instances(): Map<string, OWObject | OWObject[]> {
        return createdInstances.instances;
    }

    /**
     * Čas po kterém se periodicky posílá uvolnění instancí na službu (ms)
     */
    public static get cleanupInterval(): number {
        const mgr = _getMasterManager();
        return mgr ? mgr._cleanupInterval : InstanceManager._cleanupInterval;
    }

    public static set cleanupInterval(val: number) {
        const mgr = _getMasterManager();
        if (mgr) {
            mgr.cleanupInterval = val;
            return;
        }

        InstanceManager._cleanupInterval = val;

        if (InstanceManager._cleanupHandle) {
            clearInterval(InstanceManager._cleanupHandle);
            InstanceManager._cleanupHandle = 0;
        }

        InstanceManager.start();
    }

    /**
     * Uvolní nepoužívané instance objektů
     * @param objects Objekty k uvolnění
     */
    public static removeObject(object: IOWObject): boolean {
        if (!object)
            return false;

        const refCount = InstanceManager._getReferencesCount(object);
        if (refCount > 1) {
            logger.debug(`Decreasing reference count (from ${refCount}) for ${object.instanceID} and not releasing instance.`, object.objectName, object.id); //`
            InstanceManager._setReferencesCount(object, refCount - 1);
            return false;
        }

        createdInstances.instances.delete(object.instanceID);

        if (!object.isNew) {
            const key = InstanceManager._generateId(object.objectName, object.id);

            const existingObjects = createdInstances.instances.get(key) as IOWObject[];
            if (existingObjects != null) {
                const index = existingObjects.indexOf(object);
                if (index !== -1)
                    existingObjects.splice(index);

                if (existingObjects.length === 0)
                    createdInstances.instances.delete(key);
            }
        }

        InstanceManager._instancesToDelete.push({
            instanceID: object.instanceID,
            objectName: object.objectName,
            id: object.id,
            isNew: object.isNew,
            attributeOnParentObject: object.attributeOnParentObject,
            time: Date.now(),
        });

        logger.debug(() => `Releasing: ${InstanceManager._createInstanceDescription(object)}`); //`
        InstanceManager._printState();

        if (!InstanceManager.cleanupInterval)
            InstanceManager._cleanUpInstances();

        return true;
    }

    /**
     * Zaregistruje novou instanci aplikačního objektu
     * @param obj Aplikační objekt
     */
    public static addObject(obj: OWObject): void {
        const existingInstance = createdInstances.instances.get(obj.instanceID);
        if (existingInstance != null) {
            if (existingInstance === obj) {
                this.addInstanceUsage(obj);
            } else {
                logger.error(`Registering another client instance of a server instance which is already registered - skipped! Instance: ${InstanceManager._createInstanceDescription(obj)}`); //`
            }
            return;
        }

        if (InstanceManager._getReferencesCount(obj) !== undefined)
            throw new InvalidOperationException(`Object '${obj.objectIdentity}' instance ${obj.instanceID} has already some references (${InstanceManager._getReferencesCount(obj)})`);

        InstanceManager._setReferencesCount(obj, 1);

        createdInstances.instances.set(obj.instanceID, obj);
        InstanceManager._addPersistentObject(obj);

        logger.debug(() => `Added: ${InstanceManager._createInstanceDescription(obj)}`);
        InstanceManager._printState();
    }

    private static _createInstanceDescription(obj: LoggableInstance): string {
        const parentObject = (obj as any)._parentObject as IOWObject | undefined;
        return `${obj.isNew ? "Unsaved instance" : "Instance"} ${obj.instanceID}${!obj.isNew ? ` id ${obj.id}` : ""} of class '${obj.objectName}'${
            parentObject ? ` placed in attribute '${obj.attributeOnParentObject}' of ${parentObject.isNew ? "unsaved " : ""}object ${parentObject.instanceID}${!parentObject.isNew ? ` id ${parentObject.id}` : ""} of class '${parentObject.objectName}'` : ""
        }`; //`
    }

    private static _addPersistentObject(obj: OWObject): void {
        if (obj.isNew)
            return;

        const guid = InstanceManager._generateId(obj.objectName, obj.id);

        let objects = createdInstances.instances.get(guid) as OWObject[];
        if (objects == null) {
            objects = [];
            createdInstances.instances.set(guid, objects);
        }

        objects.push(obj);
    }

    /**
     * Zperzistní objekt i v rámci registrovaných instancí (je ho pak možné vytáhnout pomocí getObjects())
     * @param obj Aplikační objekt
     */
    public static persistObject(obj: OWObject): void {
        InstanceManager._addPersistentObject(obj);
        //logger.debug(`Persisted object id '${obj.id}' of class '${obj.objectName}'`, createdInstances.instances);
    }

    /**
     * Získá zaregistrovanou instanci aplikačního objektu nebo null, pokud takový zaregistrovaný není
     * @param instanceId Identifikátor instance aplikačního objektu
     */
    public static getInstance(instanceId: string): OWObject | null {
        return createdInstances.instances.get(instanceId) as OWObject;
    }

    /**
     * Získá všechny instance zaregistrovaného aplikačního objektu pomocí Id a názvu třídy nebo null, pokud žádná není zaregistrovaná
     * @param className Název třídy
     * @param id Identifikátor aplikačního záznamu
     */
    public static getObjects(className: string, id: string): readonly IOWObject[] | null {
        const objects = createdInstances.instances.get(InstanceManager._generateId(className, id));
        return !objects ? null : objects as OWObject[];
    }

    private static _generateId(className: string, id: string): string {
        return className + "#" + id;
    }

    /**
     * Spustí proces hlídání a uvolňování nepotřebných instancí
     */
    public static start(): void {
        if (self.parent !== self) // jen v hlavním framu běží timer
            return;

        if (InstanceManager.cleanupInterval) {
            setInterval(() => {
                InstanceManager._cleanUpInstances();
            }, InstanceManager.cleanupInterval);
        }
    }

    private static async _cleanUpInstances(): Promise<void> {
        const instances = InstanceManager._instancesToDelete;
        if (instances.length === 0)
            return;

        const minimumDisposeAge = Date.now() - InstanceManager._cleanupDelay;

        // vybere a odstraní pouze instance, které čekají déle než _cleanupDelay, ostatní čekají na další spuštění
        const instancesForRelease: TimedInstance[] = [];
        let i = instances.length;
        while (i--) {
            const ti = instances[i];
            if (ti.time > minimumDisposeAge)
                continue;

            // Pokud mezitím došlo k novému načtení instance, tak se uvolnění přeskočí a jen se odebere z kolekce instancí pro uvolnění
            if (!createdInstances.instances.has(ti.instanceID)) {
                instancesForRelease.push(ti);
            }

            instances.splice(i, 1);
        }

        if (instancesForRelease.length === 0)
            return;

        logger.debug(() => `Cleanup: ${instancesForRelease.length}, Keep: ${instances.length}, Holding: ${Array.from(InstanceManager._instances.values()).filter(i => !Array.isArray(i)).length}`); //`

        try {
            const requestContextForReleaseInstances: RequestContextWithoutTransactionContextId = {
                roles: AppContext.instance.activeUserRolesIds,
                cancellationToken: CancellationToken.none,
            };
            await OWManager.releaseInstances(ArrayUtils.getUniqueArray(instancesForRelease.map(i => i.instanceID)), requestContextForReleaseInstances);

            logger.groupCollapsed(`[InstanceManager] Requesting server to dispose following ${instancesForRelease.length} instances:`); //`
            instancesForRelease.forEach(i => logger.debug(() => InstanceManager._createInstanceDescription(i)));
            logger.groupEnd();
        } catch (e) {
            logger.error(e);
            for (const i of instancesForRelease)
                instances.push({ ...i, time: 0 });
        }
    }

    private static _printState(predicate?: (predicate: IOWObject) => boolean): void {
        let instances = Array.from(InstanceManager._instances.values()).filter(i => !Array.isArray(i)) as IOWObject[];
        if (predicate) {
            const filtered = instances.filter(i => predicate(i));
            logger.groupCollapsed(`Holds ${instances.length} instance(s) (filtered: ${filtered.length})`); //`
            instances = filtered;
        } else {
            logger.groupCollapsed(`Holds ${instances.length} instance(s)`); //`
        }
        instances.forEach(i => logger.debug(() => InstanceManager._createInstanceDescription(i)));
        logger.groupEnd();
    }

    /**
     * Zvýší počet referencí na objekt, využití při znovupoužití stejné instance
     * @param obj Aplikační objekt
     */
    public static addInstanceUsage(obj: IOWObject): void {
        const refCount = InstanceManager._getReferencesCount(obj);
        if (refCount === undefined)
            throw new InvalidOperationException(`Object '${obj.objectIdentity}' instance ${obj.instanceID} has no reference yet so it can't be incremented`);

        InstanceManager._setReferencesCount(obj, refCount + 1);
    }

    private static _getReferencesCount(obj: object): number | undefined {
        return (obj as any)[_referencesSymbol];
    }

    private static _setReferencesCount(obj: object, count: number): void {
        (obj as any)[_referencesSymbol] = count;
    }
}

InstanceManager.start();

GlobalContext.registerType(InstanceManager);
